/**
 * 统一的"退出 / 登出"事件监控。
 *
 * 用于排查"参会者莫名其妙退出系统"的问题：所有会导致用户被强制离开会议页
 * 或被登出的代码路径都应调用 logExit，记录足够的上下文（原因、路由、会议、
 * 参会者、token 状态、连接状态、错误信息），便于事后集中定位根因。
 *
 * 设计原则：
 * - 绝不抛错、绝不阻塞主流程（监控本身不能引发新的问题）；
 * - 优先用 navigator.sendBeacon，保证在页面跳转 / 卸载时也能可靠上报；
 * - 前端 console 用统一前缀 [EXIT-MONITOR] 方便过滤；后端集中记录便于监控。
 */

export type ExitReason =
    // —— 用户主动触发（正常，非异常）——
    | "manual-logout" // 主动点击登出
    | "manual-leave" // 主动点击离开会议
    // —— 异常 / 被动触发 ——
    | "no-token" // 刷新用户时未找到 token
    | "token-expired" // token 过期
    | "refresh-api-failed" // /api/user/refresh 调用失败
    | "meeting-load-failed" // 会议加载失败，跳回首页
    | "kicked-or-offline" // 被踢 / 被置离线，离开会议页
    | "meeting-ended" // 会议结束
    | "signalr-closed" // SignalR 连接关闭
    | "signalr-connect-failed" // SignalR 连接建立失败
    | "signalr-heartbeat-failed"; // 心跳连续失败触发重连

export interface ExitContext {
    meetingId?: string;
    participantName?: string;
    userId?: string;
    hasToken?: boolean;
    tokenExpiresAt?: number | null;
    connectionState?: string;
    error?: string;
}

const ABNORMAL_REASONS: ReadonlySet<ExitReason> = new Set<ExitReason>([
    "no-token",
    "token-expired",
    "refresh-api-failed",
    "meeting-load-failed",
    "kicked-or-offline",
    "meeting-ended",
    "signalr-closed",
    "signalr-connect-failed",
    "signalr-heartbeat-failed",
]);

const CLIENT_LOG_URL = "/api/diagnostics/clientLog";

const safeString = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "string") return value;
    if (value instanceof Error) return value.message;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

export const logExit = (reason: ExitReason, context: ExitContext = {}) => {
    const abnormal = ABNORMAL_REASONS.has(reason);

    const record = {
        reason,
        abnormal,
        at: new Date().toISOString(),
        url: typeof window !== "undefined" ? window.location.href : "",
        meetingId: context.meetingId,
        participantName: context.participantName,
        userId: context.userId,
        hasToken: context.hasToken,
        tokenExpiresAt: context.tokenExpiresAt,
        connectionState: context.connectionState,
        error: safeString(context.error),
    };

    // 前端 console：异常用 error，主动退出用 warn（方便区分与过滤）
    if (abnormal) {
        console.error(`[EXIT-MONITOR] ${reason}`, record);
    } else {
        console.warn(`[EXIT-MONITOR] ${reason}`, record);
    }

    // 上报后端（fire-and-forget）
    void sendToServer(record);
};

const sendToServer = async (record: Record<string, unknown>) => {
    try {
        const body = JSON.stringify(record);

        // sendBeacon 在页面跳转 / 卸载时依然可靠，优先使用
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            const ok = navigator.sendBeacon(CLIENT_LOG_URL, blob);
            if (ok) return;
        }

        // 回退到 fetch（keepalive 允许请求在页面卸载后继续）
        await fetch(CLIENT_LOG_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        });
    } catch {
        // 监控本身绝不影响主流程
    }
};

export default logExit;
